import { useAccountState } from '../../../../redux/reducers/accountReducer';
/* eslint-disable @typescript-eslint/no-explicit-any */
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import { useFormik } from 'formik';
import {
    Button,
    Dropdown,
    Menu,
    MenuButton,
    MenuItem,
    MenuList,
    MenuPopover,
    MenuTrigger,
    Option,
    OptionOnSelectData,
    Text,
    Title2,
    makeStyles,
    tokens,
} from '@fluentui/react-components';
import { Add16Filled, DismissRegular, PuzzleCubePieceRegular, TargetAddRegular } from '@fluentui/react-icons';
import { CaseInfoCard } from '../CaseInfo/CaseInfoCard';
import { AffiliatedPartiesCard } from '../CaseInfo/CaseAffiliatedPartiesCard';
import { Paths, Container, Column, Row, BrandCard, CustomCardHeader } from '../../../common';
import { useToasts } from '../../../toasts/useToasts';
import { CaseEventsCard } from '../CaseEvents/CaseEventsCard';
import { CaseNotesCard } from '../CaseNotes/CaseNotesCard';
import { CaseAssignedToCard } from '../CaseInfo/CaseAssignedToCard';
import {
    AppMessage,
    Case,
    CaseEvent,
    CaseEventTemplate,
    CaseInfo,
    CaseMilestoneTemplate,
    CaseTemplate,
    Note,
    EngagementTemplate,
    Task,
    useInitialValues,
    UserRef,
    User,
} from '../../../../data/types';
import { useCaseService } from '../../../../data/services/useCaseService';
import { AppMessageDisplay, useAppMessageDisplay } from '../../../controls/AppMessageDisplay';
import { useCaseTemplateService } from '../../../../data/services/useCaseTemplateService';
import { useEmphasize } from '../../../../utilities/useEmphasize';
import { useCaseEventService, useCaseMilestoneService, useCaseEngagementService, useTaskService } from '../../../../data/services';
import { MilestoneTemplateTable } from '../../Templates/MilestoneTemplateTable';
import { useRecentCasesService } from '../../../../data/services/useRecentCasesService';
import { PersonControl } from '../../../controls/PersonControl';
import { useAccess } from '../../../../auth/useAccess';
import { appTokens } from '../../../common/Styles/appTokens';
import { useValidation } from '../../../../data/types/validation';

const useStyles = makeStyles({
    content: {
        borderTop: `2px solid ${tokens.colorBrandBackground}`,
    },
});

export const AddCase = () => {
    //Hooks
    const { primaryAccount } = useAccountState();
    const access = useAccess(primaryAccount?.user);
    const navTo = useNavigate();
    const initValues = useInitialValues();
    const toasts = useToasts();
    const caseService = useCaseService();
    const { addRecentCaseAsync } = useRecentCasesService();
    const engagementService = useCaseEngagementService();
    const messageDisplay = useAppMessageDisplay();
    const caseTemplateService = useCaseTemplateService();
    const milestoneService = useCaseMilestoneService();
    const taskService = useTaskService();
    const eventService = useCaseEventService();
    const { validateCaseObj } = useValidation();

    const caseTypeEmphasize = useEmphasize();
    const shortDescripEmphasize = useEmphasize();
    const overviewEmphasize = useEmphasize();
    const statusEmphasize = useEmphasize();
    const notesEmphasize = useEmphasize();
    const retainedDateEmphasize = useEmphasize();
    const eventsEmphasize = useEmphasize();

    //State
    const [engagementTemplate, setEngagementTemplate] = useState<EngagementTemplate>();
    const [milestones, setMilestones] = useState<CaseMilestoneTemplate[]>([]);
    const [assignMilestoneTasksTo, setAssignMilestoneTasksTo] = useState<UserRef | User>();

    //Styles
    const styles = useStyles();

    //Form
    const hasRequiredFields = (caseValues: Case) => {
        return (
            !!caseValues.caseId && !!caseValues.caseName && !!caseValues.retainedBy?.party?.name && !!caseValues.retainedDate
        );
    };
    const caseForm = useFormik<Case>({
        initialValues: initValues.caseObjWithNotesEvents(),
        onSubmit: async (values) => {
            const caseVals = values as Case;
            if (!hasRequiredFields(caseVals)) {
                messageDisplay.addMessage(getMissingFieldsMsg(caseVals));
            } else {
                try {
                    //submit new case type
                    await caseService.upsertCaseAsync(values as Case);

                    //add as recent case
                    addRecentCaseAsync(values as CaseInfo);

                    //if an engagement template is set by case template, save it now
                    if (!!engagementTemplate) {
                        const caseEngagement = initValues.caseEngagement(values.caseId!);
                        caseEngagement.title = engagementTemplate.title;
                        caseEngagement.fields = engagementTemplate?.fields;
                        caseEngagement.addSignatures = engagementTemplate.addSignatures;

                        await engagementService.upsertCaseEngagementDocAsync(caseEngagement);
                    }

                    //if milestones are set by case template, save the milestones
                    if (!!milestones.length) {
                        milestoneService.createMilestonesForCase(
                            milestones,
                            values.caseId!,
                            upsertTasks,
                            upsertEvents,
                            assignMilestoneTasksTo?.userId,
                        );
                    }

                    toasts.cases.notifyCaseCreated(values as Case);
                } catch {
                    toasts.cases.notifyCaseCreationError(values as Case);
                }

                navTo(Paths.cases);
            }
        },
        validationSchema: validateCaseObj.schema,
    });

    //Access
    const canCreateCaseTemplates = () => {
        return primaryAccount?.account?.features?.templates && access.templates.canCreateEditCaseTemplates();
    };
    const hasTemplatesFeature = () => {
        return primaryAccount?.account?.features?.templates;
    };

    //Messages
    const getMissingFieldsMsg = (caseVals: Case): AppMessage => {
        return {
            id: 'missingFields',
            intent: 'error',
            title: 'Missing required fields:',
            content: (
                <>
                    {!caseVals.caseName && (
                        <Text>
                            <Text italic>'Case Name'</Text> is a required field.{' '}
                        </Text>
                    )}
                    {!caseVals.retainedBy?.party?.name && (
                        <Text>
                            <Text italic>'Retained by'</Text> is a required field.{' '}
                        </Text>
                    )}
                    {!caseVals.retainedDate && (
                        <Text>
                            <Text italic>'Retained Date'</Text> is a required field.{' '}
                        </Text>
                    )}
                </>
            ),
        };
    };

    //Logic
    const upsertTasks = async (tasks: Task[]) => {
        await Promise.all(tasks.map((t) => taskService.upsertTaskAsync(t)));
    };
    const upsertEvents = async (events: CaseEvent[]) => {
        await Promise.all(events.map((evt) => eventService.upsertCaseEventAsync(evt)));
    };

    //UI Events
    const clickCreateCase = (e: React.MouseEvent) => {
        e.stopPropagation();
        caseForm.submitForm();
    };
    const clickCancel = (e: React.MouseEvent) => {
        e.stopPropagation();
        navTo(Paths.cases);
    };
    const onSelectUserForMilestoneTasks = (_ev: any, data: OptionOnSelectData) => {
        const acctUser = primaryAccount?.accountUsers.find((x) => x.username == data.optionValue);
        if (acctUser) {
            setAssignMilestoneTasksTo(acctUser);
        }
    };
    const clickApplyTemplate = (template: CaseTemplate, ev?: any) => {
        ev?.stopPropagation();

        //set fields
        caseForm.setFieldValue('retainedDate', new Date());
        caseForm.setFieldValue('caseType', template.caseType);
        caseForm.setFieldValue('shortDescription', template.shortDescription);
        caseForm.setFieldValue('overview', template.overview);
        caseForm.setFieldValue('allowedStatuses', template.statuses);
        caseForm.setFieldValue('status', template.statuses?.[0]);

        //set the engagement template
        if (!!template.engagementTemplate) {
            setEngagementTemplate(template.engagementTemplate);
        }

        //set note
        const note: Note = {
            ...initValues.caseNote(caseForm.values.caseId!),
            text: template.notes?.[0] || '',
            lifecycle: {
                createdOn: new Date(),
                createdBy: primaryAccount?.user.username,
            },
        };
        if (!!template.notes?.[0]) {
            caseForm.setFieldValue('caseNotes', [note]);

            notesEmphasize.emphasizeElement();
        }

        //set events
        let events = [] as CaseEvent[] | undefined;
        events = template.events?.map((evt: CaseEventTemplate) => {
            const eventDate = new Date();
            eventDate.setDate(eventDate.getDate() + (evt.daysFromRetained || 0));

            return {
                ...initValues.caseEvent(caseForm.values.caseId!),
                event: evt.eventName,
                description: evt.description,
                date: eventDate,
                due: evt.due,
            } as CaseEvent;
        });
        if (!!events?.length) {
            caseForm.setFieldValue('caseEvents', events);
            eventsEmphasize.emphasizeElement();
        }

        //save milestones
        setMilestones(template.milestones || []);

        //trigger emphasis on changed fields
        caseTypeEmphasize.emphasizeElement();
        shortDescripEmphasize.emphasizeElement();
        overviewEmphasize.emphasizeElement();
        statusEmphasize.emphasizeElement();
        retainedDateEmphasize.emphasizeElement();
    };

    //Effects
    useEffect(() => {
        if (!!primaryAccount?.user) {
            setAssignMilestoneTasksTo(primaryAccount?.user);
        }
    }, [!!primaryAccount?.user]);

    return (
        <>
            <Container>
                <Column flex='1'>
                    <Row>
                        <Column noGap width='auto'>
                            {caseForm.values?.retainedBy?.party?.name && (
                                <Text size={400}>{caseForm.values?.retainedBy?.party?.name}</Text>
                            )}
                            <Title2>{!caseForm?.values.caseName ? 'New Case' : `${caseForm?.values.caseName || ''}`}</Title2>
                        </Column>
                        <Column>
                            <AppMessageDisplay messages={messageDisplay.messages} onDismiss={messageDisplay.removeMessage} />
                        </Column>
                        <Column align='right' vAlign='middle' width='auto'>
                            <Row noWrap>
                                {hasTemplatesFeature() && (
                                    <Menu positioning='below-end'>
                                        <MenuTrigger>
                                            <MenuButton
                                                icon={<PuzzleCubePieceRegular />}
                                                data-testid='add-case-apply-template'
                                            >
                                                Apply Template
                                            </MenuButton>
                                        </MenuTrigger>
                                        <MenuPopover>
                                            <MenuList>
                                                {caseTemplateService.caseTemplates.length ? (
                                                    caseTemplateService.caseTemplates.map((ct: CaseTemplate) => (
                                                        <MenuItem
                                                            key={ct.templateId}
                                                            onClick={(ev) => clickApplyTemplate(ct, ev)}
                                                            data-testid={`add-case-apply-template-${ct.templateId}`}
                                                        >
                                                            {ct.templateName}
                                                        </MenuItem>
                                                    ))
                                                ) : (
                                                    <>
                                                        {canCreateCaseTemplates() ? (
                                                            <MenuItem
                                                                onClick={() => navTo(Paths.templates)}
                                                                data-testid='add-case-apply-template-no-templates'
                                                            >
                                                                <Column noGap align='center'>
                                                                    <Text size={300}>No templates available.</Text>
                                                                    <Text size={300}>
                                                                        Click here to create case templates.
                                                                    </Text>
                                                                </Column>
                                                            </MenuItem>
                                                        ) : (
                                                            <Text size={300}> No templates available</Text>
                                                        )}
                                                    </>
                                                )}
                                            </MenuList>
                                        </MenuPopover>
                                    </Menu>
                                )}
                                    <Button
                                        appearance='primary'
                                        icon={<Add16Filled />}
                                        onClick={clickCreateCase}
                                        disabled={!caseForm.isValid || !caseForm.dirty}
                                        data-testid='add-case-create-button'
                                    >
                                        Create
                                    </Button>

                                    <Button
                                        appearance='secondary'
                                        icon={<DismissRegular />}
                                        onClick={clickCancel}
                                        data-testid='add-case-cancel-button'
                                    >
                                        Cancel
                                    </Button>
                            </Row>
                        </Column>
                    </Row>

                    <Column className={styles.content} height={appTokens.pageHeight('4rem')} padding='px-3 py-3' scroll>
                        <Row>
                            <Column flex='1'>
                                <CaseInfoCard
                                    caseForm={caseForm}
                                    caseTypeClassName={caseTypeEmphasize.emphasizeClasses}
                                    shortDescriptionClassName={shortDescripEmphasize.emphasizeClasses}
                                    overviewClassName={overviewEmphasize.emphasizeClasses}
                                    statusClassName={statusEmphasize.emphasizeClasses}
                                    retainedDateClassName={retainedDateEmphasize.emphasizeClasses}
                                    createMode
                                />
                            </Column>
                        </Row>
                        {!!milestones.length && (
                            <BrandCard>
                                <CustomCardHeader
                                    title='Milestones'
                                    icon={<TargetAddRegular />}
                                    action={
                                        <>
                                            <Row noGap>
                                                <Column vAlign='middle' width='auto' padding='pr-3'>
                                                    <Text>Assign Tasks To:</Text>
                                                </Column>
                                                <Column vAlign='middle' width='auto'>
                                                    <Dropdown
                                                        value={assignMilestoneTasksTo?.name}
                                                        selectedOptions={[assignMilestoneTasksTo?.username || '']}
                                                        onOptionSelect={onSelectUserForMilestoneTasks}
                                                        data-testid='add-case-assign-tasks-dropdown'
                                                    >
                                                        {primaryAccount?.accountUsers.map((acctUser: UserRef | User) => (
                                                            <Option
                                                                key={acctUser.username}
                                                                value={acctUser.username}
                                                                text={acctUser.name || ''}
                                                                data-testid={`add-case-assign-tasks-dropdown-option-${acctUser.username}`}
                                                            >
                                                                <PersonControl size='option' person={acctUser} />
                                                            </Option>
                                                        ))}
                                                    </Dropdown>
                                                </Column>
                                            </Row>
                                        </>
                                    }
                                />
                                <MilestoneTemplateTable milestoneTemplates={milestones} showActions={false} />
                            </BrandCard>
                        )}
                        <Row>
                            <Column flex='2'>
                                <CaseAssignedToCard caseForm={caseForm} createMode />
                            </Column>
                            <Column flex='3'>
                                <AffiliatedPartiesCard caseForm={caseForm} createMode />
                            </Column>
                        </Row>
                        <Row>
                            <Column flex='2'>
                                <CaseNotesCard caseForm={caseForm} notePanelClassName={notesEmphasize.emphasizeClasses} />
                            </Column>
                            <Column flex='3'>
                                <CaseEventsCard caseForm={caseForm} eventPanelClassName={eventsEmphasize.emphasizeClasses} />
                            </Column>
                        </Row>
                    </Column>
                </Column>
            </Container>
        </>
    );
};
